/*
 * sessionPlan — turns "what, which formats, how long" into the ordered list of
 * activities a practice session runs through (SessionRunner).
 *
 * Counts come from a rough per-item time cost so a 30-minute session with
 * MCQs, flashcards, theory and a case study lands near 5 · 5 · 3 · 1.
 * The step order is always interleaved (see buildSteps).
 */
export const ACTIVITIES = [
  { id: 'mcq', label: 'MCQs', one: 'MCQ', blurb: 'Quick concept checks and fundamentals', minutes: 1.4, max: 20 },
  { id: 'flashcards', label: 'Flashcards', one: 'Flashcard', blurb: 'Revise key concepts and improve recall', minutes: 1.4, max: 20 },
  { id: 'theory', label: 'Theory Questions', one: 'Theory question', blurb: 'Explain concepts in your own words', minutes: 2.4, max: 10 },
  { id: 'case', label: 'Case Studies', one: 'Case study', blurb: 'Real-world scenarios, coached by AI', minutes: 9, max: 3 },
  { id: 'rapid', label: 'AI Rapid Fire', one: 'Rapid-fire question', blurb: 'Quick, one-line questions', minutes: 0.6, max: 25 },
];
export const actOf = (id) => ACTIVITIES.find((a) => a.id === id);

export const LENGTHS = [
  { min: 15, label: '~15 min' },
  { min: 30, label: '~30 min' },
  { min: 45, label: '~45 min' },
  { min: 60, label: '~60 min' },
];

/** How many of each selected activity fit in `minutes`. */
export const allocate = (types, minutes) => {
  const sel = ACTIVITIES.filter((a) => types.includes(a.id));
  if (!sel.length) return {};
  const out = {};
  let budget = minutes;
  const cs = sel.find((a) => a.id === 'case');
  if (cs) {
    out.case = Math.min(cs.max, Math.max(1, Math.floor(minutes / 30)));
    budget -= out.case * cs.minutes;
  }
  const rest = sel.filter((a) => a.id !== 'case');
  const share = rest.length ? Math.max(0, budget) / rest.length : 0;
  rest.forEach((a) => { out[a.id] = Math.max(2, Math.min(a.max, Math.round(share / a.minutes))); });
  return out;
};
export const totalItems = (counts) => Object.values(counts).reduce((s, n) => s + n, 0);
export const estMinutes = (counts) => Math.round(Object.entries(counts).reduce((s, [k, n]) => s + n * actOf(k).minutes, 0));

/*
 * The ordered step list: [{ type, n (1-based within type), of }].
 *
 * Every session is interleaved — formats are spread across the whole run, not
 * played as blocks. Each item of a type sits at an evenly spaced point along
 * the session; a small per-type phase puts recall (flashcards) before checks
 * (MCQ, rapid fire) before explaining (theory) before applying (case), so a
 * concept tends to be recalled, then tested, then explained or used. A final
 * pass breaks up back-to-back repeats whenever another format is available.
 * Only the types present in `counts` ever appear.
 */
const PHASE = { flashcards: 0.15, mcq: 0.3, rapid: 0.4, theory: 0.55, case: 0.75 };
export const buildSteps = (counts) => {
  const items = [];
  ACTIVITIES.forEach(({ id }) => {
    const n = counts[id] || 0;
    for (let i = 0; i < n; i += 1) items.push({ type: id, n: i + 1, of: n, at: (i + PHASE[id]) / n });
  });
  items.sort((a, b) => a.at - b.at || PHASE[a.type] - PHASE[b.type]);
  // break up runs: pull the nearest later item of a different type forward
  for (let k = 1; k < items.length; k += 1) {
    if (items[k].type !== items[k - 1].type) continue;
    const j = items.findIndex((x, i) => i > k && x.type !== items[k - 1].type);
    if (j < 0) break;                                   // only one format left — nothing to mix in
    if (j - k > 1) continue;                            // don't drag an item far from its place (a lone case study stays late)
    items.splice(k, 0, items.splice(j, 1)[0]);
  }
  // keep each type's numbering in sequence after the moves
  const seen = {};
  return items.map(({ type, of }) => { seen[type] = (seen[type] || 0) + 1; return { type, n: seen[type], of }; });
};

/** Recommended formats for a learner: always recall + check + explain; case when there's time; rapid fire for due reviews. */
export const recommendedTypes = ({ minutes, dueCount = 0 }) => {
  const t = ['flashcards', 'mcq', 'theory'];
  if (minutes >= 30) t.push('case');
  if (dueCount > 0) t.push('rapid');
  return t;
};

const RUN_KEY = 'pu_run_v1';
export const saveRun = (run) => { try { sessionStorage.setItem(RUN_KEY, JSON.stringify(run)); } catch { /* ignore */ } };
export const loadRun = () => { try { return JSON.parse(sessionStorage.getItem(RUN_KEY) || 'null'); } catch { return null; } };
export const clearRun = () => { try { sessionStorage.removeItem(RUN_KEY); } catch { /* ignore */ } };

/* Completed sessions, kept in this browser (like interview history) so the
   Practice page can list them and offer "practise again" with the same setup. */
const HISTORY_KEY = 'pu_sessions_v1';
export const loadSessionHistory = () => { try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]') || []; } catch { return []; } };
export const saveSessionRecord = (rec) => {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify([rec, ...loadSessionHistory().filter((r) => r.id !== rec.id)].slice(0, 20))); } catch { /* private mode */ }
};
